/* eslint-disable no-nested-ternary */
/* eslint-disable max-params */
import { type DSHookFloatingContextT } from '../react-desc-prop-types.js';
import computeOffsets from './computeOffsets.js';

const paddingObject = { top: 0, right: 0, bottom: 0, left: 0 };

type RectLike = { top: number; left: number; right: number; bottom: number };

function rectToClientRect(rect: DOMRect) {
  return { ...rect, left: rect.x, top: rect.y, right: rect.x + rect.width, bottom: rect.y + rect.height };
}

function domRectToObject(rect: {
  x: number;
  y: number;
  width: number;
  height: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
}): DOMRect {
  return {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
    toJSON: () => {},
  };
}

export const detectOverflow = (
  referenceRect: DOMRect,
  floatingRect: DOMRect,
  placement: DSHookFloatingContextT.PopperPlacementsT,
  customOffset: [number, number],
  clippingClientRect?: RectLike,
) => {
  const basePlacement = placement.split('-')[0];
  const isVertical = ['top', 'bottom'].indexOf(basePlacement) >= 0;

  const clipping: RectLike = clippingClientRect ?? {
    top: 0,
    left: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
  };

  const popperOffsets = computeOffsets(placement, referenceRect, floatingRect);
  const popperClientRect = rectToClientRect({ ...domRectToObject(floatingRect), ...popperOffsets });

  const overflowOffsets = {
    top: clipping.top - popperClientRect.top + paddingObject.top,
    bottom: popperClientRect.bottom - clipping.bottom + paddingObject.bottom,
    left: clipping.left - popperClientRect.left + paddingObject.left,
    right: popperClientRect.right - clipping.right + paddingObject.right,
  };

  const offset = {
    x: basePlacement === 'left' ? -(customOffset?.[0] ?? 12) : (customOffset?.[0] ?? 12),
    // FIX: default must be 12, not -12 (otherwise 'top' flips)
    y: basePlacement === 'top' ? -(customOffset?.[1] ?? 12) : (customOffset?.[1] ?? 12),
  };

  // if NOT vertical, switch x for y and vice versa
  if (!isVertical) {
    const temp = offset.x;
    offset.x = offset.y * (basePlacement === 'right' ? 1 : -1);
    offset.y = temp;
  }

  (Object.keys(overflowOffsets) as Array<keyof typeof overflowOffsets>).forEach((key) => {
    const multiply = key === 'right' || key === 'bottom' ? 1 : -1;
    const axis = key === 'top' || key === 'bottom' ? 'y' : 'x';
    overflowOffsets[key] += offset[axis] * multiply;
  });

  return overflowOffsets;
};
